<template lang="pug">
div
  JobLogModal(ref="jobLogModal")

  base-header(ref="base")
    b-row.py-3(align-v="center")
      b-col(cols="12")
        h6.h2.text-white.d-inline-block.mb-0
          i.fa.fa-tasks.text-muted.mr-2
          | Jobs Übersicht
        .small.text-muted(v-if="jobs && jobs.length > 0") {{ jobs.length }} Jobs

  b-container(fluid).mt-4
    b-card
      b-row.mb-3
        b-col(cols="12")
          .d-flex.justify-content-between.align-items-center
            h5.mb-0 Letzte 50 Jobs
            b-button(
              variant="outline-primary",
              size="sm",
              @click="fetchJobs",
              :disabled="loading"
            )
              i.fa.fa-refresh.mr-1(:class="{ 'fa-spin': loading }")
              | Aktualisieren

      // Jobs Table
      b-table(
        :items="jobs",
        :fields="fields",
        :busy="loading",
        striped,
        hover,
        responsive,
        show-empty,
        empty-text="Keine Jobs gefunden",
        @row-clicked="openJobLog"
      )
        template(#table-busy)
          .text-center.my-4
            b-spinner.mr-2
            | Lade Jobs...

        template(#cell(status)="data")
          b-badge(
            :variant="getStatusVariant(data.value)"
          ) {{ getStatusLabel(data.value) }}

        template(#cell(action)="data")
          b-badge(variant="secondary") {{ getActionLabel(data.value) }}

        template(#cell(progress)="data")
          .d-flex.align-items-center(v-if="data.item.total_documents")
            b-progress.flex-grow-1.mr-2(
              :value="data.item.progress",
              :max="data.item.total_documents",
              :variant="getProgressVariant(data.item)",
              height="8px",
              :animated="data.item.status === 'IN_PROGRESS'"
            )
            small {{ data.item.progress }} / {{ data.item.total_documents }}
          span(v-else) -

        template(#cell(created_at)="data")
          span {{ formatDate(data.value, data.item) }}

        template(#cell(project_name)="data")
          span.text-truncate(style="max-width: 200px; display: inline-block;") {{ data.value || '-' }}

        template(#cell(owner)="data")
          span(v-if="data.value && data.value.email") {{ data.value.email }}
          span(v-else) -

        template(#cell(actions)="data")
          b-button-group(size="sm")
            b-button(
              variant="outline-primary",
              @click.stop="openJobLog(data.item)",
              v-b-tooltip.hover,
              title="Log anzeigen"
            )
              i.fa.fa-file-text
            b-button(
              variant="outline-danger",
              @click.stop="confirmDeleteJob(data.item)",
              v-b-tooltip.hover,
              title="Job abbrechen",
              :disabled="data.item.status !== 'IN_PROGRESS'"
            )
              i.fa.fa-times

</template>

<script>
import JobLogModal from './JobLogModal.vue'

export default {
  name: 'Jobs',
  components: {
    JobLogModal
  },
  data() {
    return {
      jobs: [],
      loading: false,
      fields: [
        { key: 'status', label: 'Status', sortable: true },
        { key: 'action', label: 'Typ', sortable: true },
        { key: 'project_name', label: 'Projekt', sortable: true },
        { key: 'progress', label: 'Fortschritt' },
        { key: 'owner', label: 'Erstellt von' },
        { key: 'created_at', label: 'Erstellt am', sortable: true },
        { key: 'actions', label: 'Aktionen' }
      ]
    }
  },
  mounted() {
    this.fetchJobs()
  },
  methods: {
    async fetchJobs() {
      this.loading = true
      try {
        const response = await this.axios.get(this.base_url + '/jobs/all?limit=50')
        this.jobs = response.data
      } catch (error) {
        console.error('Error fetching jobs:', error)
        this.$bvToast.toast('Fehler beim Laden der Jobs', {
          title: 'Fehler',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.loading = false
      }
    },
    openJobLog(job) {
      this.$refs.jobLogModal.open(job)
    },
    async confirmDeleteJob(job) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Wollen Sie den Job "${job.project_name || job._id}" wirklich abbrechen?`,
        {
          title: 'Job abbrechen',
          okVariant: 'danger',
          okTitle: 'Abbrechen',
          cancelTitle: 'Zurück',
          centered: true
        }
      )
      if (confirmed) {
        this.deleteJob(job)
      }
    },
    async deleteJob(job) {
      try {
        await this.axios.delete(this.base_url + '/job/' + job._id)
        this.$bvToast.toast('Job wurde abgebrochen', {
          title: 'Erfolg',
          variant: 'success',
          solid: true
        })
        this.fetchJobs()
      } catch (error) {
        console.error('Error deleting job:', error)
        this.$bvToast.toast('Fehler beim Abbrechen des Jobs', {
          title: 'Fehler',
          variant: 'danger',
          solid: true
        })
      }
    },
    getStatusVariant(status) {
      switch (status) {
        case 'DONE':
          return 'success'
        case 'IN_PROGRESS':
          return 'info'
        case 'DELETED':
          return 'danger'
        default:
          return 'secondary'
      }
    },
    getStatusLabel(status) {
      switch (status) {
        case 'DONE':
          return 'Fertig'
        case 'IN_PROGRESS':
          return 'In Bearbeitung'
        case 'DELETED':
          return 'Abgebrochen'
        default:
          return status
      }
    },
    getActionLabel(action) {
      switch (action) {
        case 'IMPORT':
          return 'Import'
        case 'EXPORT':
          return 'Export'
        case 'EXTRACT':
          return 'Extraktion'
        default:
          return action
      }
    },
    getProgressVariant(job) {
      if (job.status === 'DONE') return 'success'
      if (job.status === 'DELETED') return 'danger'
      return 'info'
    },
    formatDate(dateStr, item) {
      // Try to get date from created_at first, fallback to MongoDB ObjectId timestamp
      let date = null
      
      if (dateStr) {
        date = new Date(dateStr)
      }
      
      // If date is invalid or seems wrong (e.g., same for all items due to Python default bug),
      // extract timestamp from MongoDB ObjectId (_id)
      if (item && item._id && (!date || isNaN(date.getTime()))) {
        // MongoDB ObjectId: first 8 hex chars = Unix timestamp
        const timestamp = parseInt(item._id.substring(0, 8), 16) * 1000
        date = new Date(timestamp)
      }
      
      if (!date || isNaN(date.getTime())) return '-'
      
      return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.table tbody tr {
  cursor: pointer;
}
.table tbody tr:hover {
  background-color: rgba(0, 123, 255, 0.05);
}
</style>
